import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MessageCircle, MapPin, Send, CheckCircle2, Loader2 } from "lucide-react";
import { MaskLines, Overline, Reveal } from "./Motion";
import { WORKSPACES, LOCATIONS, OWNER, PHONE_DISPLAY, PHONE_TEL, WA_DEFAULT, waLink } from "../../lib/site";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;
const LOC_OPTS = [...LOCATIONS.map((l) => `${l.name} – Sector ${l.no}`), "Either location"];
const EMPTY = { name: "", phone: "", email: "", location: LOC_OPTS[0], workspace: "", team_size: "", visit_date: "", message: "" };

const field = "w-full rounded-xl border border-white/10 bg-[#0B0F17] px-4 py-3.5 text-[15px] text-white placeholder:text-slate-500 outline-none transition-[border-color,box-shadow] focus:border-[#D4AF37]/60 focus:shadow-[0_0_0_4px_rgba(212,175,55,.12)]";
const Label = ({ children, htmlFor }) => <label htmlFor={htmlFor} className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-slate-400">{children}</label>;

const buildWa = (f) =>
  waLink(`Hi Ajay, I'm ${f.name || "interested in Kinara Works"}.
Workspace: ${f.workspace || "Not decided yet"}
Location: ${f.location}
Team size: ${f.team_size || "-"}
Preferred visit: ${f.visit_date || "-"}
Phone: ${f.phone || "-"}
${f.message}`.trim());

const Info = () => (
  <div className="lg:col-span-5">
    <Overline>Contact & Booking</Overline>
    <MaskLines className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.03em] leading-[1.02] text-white"
      lines={["Book a visit.", <span className="text-gold">We'll handle the rest.</span>]} />
    <Reveal delay={0.1} className="mt-10 space-y-4">
      <a href={PHONE_TEL} data-testid="contact-call-link" className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-[#131924] p-5 hover:border-[#D4AF37]/40 transition-colors">
        <span className="grid h-12 w-12 place-items-center rounded-xl bg-[#D4AF37]/10 text-[#D4AF37]"><Phone className="h-5 w-5" /></span>
        <span><span className="block text-xs uppercase tracking-[0.15em] text-slate-400">Call {OWNER}</span><span className="font-display text-lg text-white">{PHONE_DISPLAY}</span></span>
      </a>
      <a href={WA_DEFAULT} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link" className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-[#131924] p-5 hover:border-emerald-400/40 transition-colors">
        <span className="grid h-12 w-12 place-items-center rounded-xl bg-emerald-500/10 text-emerald-400"><MessageCircle className="h-5 w-5" /></span>
        <span><span className="block text-xs uppercase tracking-[0.15em] text-slate-400">WhatsApp</span><span className="font-display text-lg text-white">Chat instantly</span></span>
      </a>
      {LOCATIONS.map((l) => (
        <div key={l.id} className="flex items-start gap-4 px-2 pt-2">
          <MapPin className="mt-1 h-4 w-4 shrink-0 text-[#D4AF37]" />
          <p className="text-sm text-slate-300"><span className="text-white">{l.name}</span><br />{l.area}</p>
        </div>
      ))}
    </Reveal>
  </div>
);

const Success = ({ form, reset }) => (
  <div data-testid="contact-success" className="flex h-full flex-col items-start justify-center py-10">
    <CheckCircle2 className="h-12 w-12 text-emerald-400" />
    <h3 className="font-display mt-6 text-3xl text-white">Thank you, {form.name.split(" ")[0]}.</h3>
    <p className="mt-3 max-w-md text-slate-300">Your enquiry has been received. Ajay's team will call you shortly. Want a faster reply? Send it on WhatsApp in one tap.</p>
    <div className="mt-8 flex flex-wrap gap-3">
      <a href={buildWa(form)} target="_blank" rel="noreferrer" data-testid="contact-success-whatsapp"
        className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-3.5 text-sm font-semibold text-[#04120B] hover:bg-emerald-400 transition-colors">
        <MessageCircle className="h-4 w-4" /> Send on WhatsApp
      </a>
      <button onClick={reset} data-testid="contact-new-enquiry" className="rounded-full border border-white/15 px-6 py-3.5 text-sm text-white hover:bg-white/5">New enquiry</button>
    </div>
  </div>
);

export const Contact = ({ prefill }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!prefill) return;
    setDone(false);
    setForm((f) => ({ ...f, ...(prefill.workspace ? { workspace: prefill.workspace } : {}), ...(prefill.location ? { location: prefill.location } : {}) }));
  }, [prefill]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!API) { toast.error("Online booking is unavailable. Please call or WhatsApp us."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setDone(true);
      toast.success("Enquiry sent successfully");
    } catch {
      toast.error("Could not send right now. Please try WhatsApp or call us.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-[#0B0F17] py-24 lg:py-36">
      <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
        <Info />
        <Reveal className="lg:col-span-7 rounded-[28px] border border-white/10 bg-gradient-to-b from-[#131924] to-[#0F141D] p-6 sm:p-10">
          {done ? <Success form={form} reset={() => { setForm(EMPTY); setDone(false); }} /> : (
            <form onSubmit={submit} data-testid="contact-form" className="grid gap-5 sm:grid-cols-2">
              <div><Label htmlFor="f-name">Full Name *</Label><input id="f-name" required minLength={2} value={form.name} onChange={set("name")} placeholder="Your name" className={field} data-testid="contact-form-name-input" /></div>
              <div><Label htmlFor="f-phone">Phone / WhatsApp *</Label><input id="f-phone" required type="tel" minLength={7} value={form.phone} onChange={set("phone")} placeholder="+91" className={field} data-testid="contact-form-phone-input" /></div>
              <div className="sm:col-span-2"><Label htmlFor="f-email">Email</Label><input id="f-email" type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={field} data-testid="contact-form-email-input" /></div>
              <div><Label htmlFor="f-loc">Preferred Location</Label>
                <select id="f-loc" value={form.location} onChange={set("location")} className={field} data-testid="contact-form-location-select">
                  {LOC_OPTS.map((o) => <option key={o}>{o}</option>)}
                </select></div>
              <div><Label htmlFor="f-ws">Workspace Type</Label>
                <select id="f-ws" value={form.workspace} onChange={set("workspace")} className={field} data-testid="contact-form-workspace-select">
                  <option value="">Not sure yet</option>
                  {WORKSPACES.map((w) => <option key={w.slug}>{w.name}</option>)}
                </select></div>
              <div><Label htmlFor="f-team">Team Size</Label><input id="f-team" value={form.team_size} onChange={set("team_size")} placeholder="e.g. 1, 5, 20+" className={field} data-testid="contact-form-team-input" /></div>
              <div><Label htmlFor="f-date">Preferred Visit Date</Label><input id="f-date" type="date" value={form.visit_date} onChange={set("visit_date")} className={`${field} [color-scheme:dark]`} data-testid="contact-form-date-input" /></div>
              <div className="sm:col-span-2"><Label htmlFor="f-msg">Message</Label><textarea id="f-msg" rows={4} value={form.message} onChange={set("message")} placeholder="Tell us what you need" className={`${field} resize-none`} data-testid="contact-form-message-input" /></div>
              <div className="sm:col-span-2 flex flex-col sm:flex-row gap-3 pt-2">
                <button type="submit" disabled={busy} data-testid="contact-form-submit-btn"
                  className="btn-gold inline-flex flex-1 items-center justify-center gap-2 rounded-full px-6 py-4 text-sm font-semibold disabled:opacity-70">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send Enquiry
                </button>
                <a href={buildWa(form)} target="_blank" rel="noreferrer" data-testid="contact-form-whatsapp-btn"
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-emerald-400/40 px-6 py-4 text-sm font-medium text-emerald-300 hover:bg-emerald-500/10 transition-colors">
                  <MessageCircle className="h-4 w-4" /> Send via WhatsApp
                </a>
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
